// lib
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import ADND from '@aytm/lib/adnd/ADND'
import uploadImageAssets from '@aytm/lib/rfd-ui/components/assets/jobs/image-asset-upload-manager'
import uploadVideoAssets from '@aytm/lib/rfd-ui/components/assets/jobs/video-asset-upload-manager'

// styles
import style from '@aytm/lib/rfd-ui/styles/assets/asset-files-uploader.module.scss'

export default class AssetFilesUploader extends Component {
  static propTypes = {
    type: PropTypes.oneOf(['image', 'video']),
    onFilesUploaded: PropTypes.func.isRequired,
    onFilesUpload: PropTypes.func,
    watchProgress: PropTypes.bool,
    multiple: PropTypes.bool,
    withOriginal: PropTypes.bool,
    withErrors: PropTypes.bool,
    maxItems: PropTypes.number,
    selectOnClick: PropTypes.bool,
    content: PropTypes.func.isRequired
  }

  constructor(...args) {
    super(...args)
    this.connectDropTarget = this.connectDropTarget.bind(this)
    this.onAssetFileDragEnter = this.onAssetFileDragEnter.bind(this)
    this.onAssetFileDragLeave = this.onAssetFileDragLeave.bind(this)
    this.acceptAssetFileDrop = this.acceptAssetFileDrop.bind(this)
    this.onAssetFileDrop = this.onAssetFileDrop.bind(this)
    this.inputFilesSelected = this.inputFilesSelected.bind(this)
    this.onDropFiles = this.onDropFiles.bind(this)

    this.dropAssetFileTarget = ADND.targets
      .files()
      .onDrop(this.onAssetFileDrop)
      .onEnter(this.onAssetFileDragEnter)
      .onLeave(this.onAssetFileDragLeave)
      .acceptDrop(this.acceptAssetFileDrop)
  }

  state = {
    dragOver: false,
    acceptFiles: true,
    progress: null
  }

  inputRef = React.createRef()

  connectDropTarget(ref) {
    this.dropAssetFileTarget.connect(ref)
  }

  componentDidMount() {
    this.mounted = true
  }

  componentWillUnmount() {
    this.mounted = false
    this.dropAssetFileTarget.disconnect()
  }

  onAssetFileDragEnter() {
    this.setState(state => {
      return {
        ...state,
        dragOver: true
      }
    })
  }

  onAssetFileDragLeave() {
    this.setState(state => {
      return {
        ...state,
        dragOver: false
      }
    })
  }

  acceptAssetFileDrop() {
    const { acceptFiles = true } = this.state
    return acceptFiles
  }

  onAssetFileDrop(...files) {
    const { multiple = false, maxItems = null } = this.props

    if (files.length) {
      this.onDropFiles(multiple ? (maxItems ? Array.prototype.slice.call(files, 0, maxItems) : files) : [files[0]])
    }
  }

  inputFilesSelected() {
    if (this.inputRef.current && this.inputRef.current.files && this.inputRef.current.files.length) {
      const { multiple = false, maxItems = null } = this.props
      this.onDropFiles(
        multiple
          ? Array.apply(
              null,
              maxItems
                ? Array.prototype.slice.call(this.inputRef.current.files, 0, maxItems)
                : this.inputRef.current.files
            )
          : [this.inputRef.current.files[0]]
      )
    }
  }

  onDropFiles(files) {
    const { type, watchProgress = true, withErrors = false, onFilesUpload, onFilesUploaded } = this.props

    if ('function' === typeof onFilesUploaded) {
      this.setState(
        state => {
          return {
            ...state,
            acceptFiles: false,
            progress: watchProgress
              ? {
                  percents: 0,
                  totalFiles: files.length,
                  loadedFiles: 0,
                  totalSize: files.reduce((totalSize, file) => totalSize + (file.size || 0), 0),
                  loadedSize: 0
                }
              : null
          }
        },
        () => {
          const uploadAsset = 'video' === type ? uploadVideoAssets : uploadImageAssets
          if ('function' === typeof onFilesUpload) {
            onFilesUpload()
          }
          uploadAsset(
            files,
            watchProgress
              ? progress => {
                  if (this.mounted) {
                    this.setState(state => {
                      return {
                        ...state,
                        progress
                      }
                    })
                  }
                }
              : null,
            withErrors
          ).then(assets => {
            const { withOriginal = false } = this.props
            if (this.mounted) {
              this.setState(
                state => {
                  return {
                    ...state,
                    acceptFiles: true,
                    progress: null
                  }
                },
                () => {
                  onFilesUploaded(
                    assets.map(asset => {
                      if ('video' === type) {
                        const videoAsset = {
                          assetUid: asset.uid,
                          videoPath: asset.original ? asset.original.mp4 : null,
                          thumbnailPath: asset.original ? asset.original.thumbnail : null
                        }
                        withOriginal && (videoAsset.original = asset.original || null)
                        return videoAsset
                      } else {
                        const imageAsset = {
                          assetUid: asset.uid,
                          imageUrl: asset.original ? asset.original.url : null,
                          imagePath: asset.original ? asset.original.path : null
                        }
                        withOriginal && (imageAsset.original = asset.original || null)
                        withErrors && asset.error && (imageAsset.error = asset.error)
                        withErrors && asset.status && (imageAsset.status = asset.status)
                        return imageAsset
                      }
                    })
                  )
                }
              )
            }
          })
        }
      )
    }
  }

  render() {
    const { type, multiple = false, content, selectOnClick = true } = this.props
    const { acceptFiles = true, dragOver, progress } = this.state
    return (
      <label
        className={`${style.component}${selectOnClick ? ' ' + style.selectOnClick : ''}`}
        ref={this.connectDropTarget}
      >
        {selectOnClick && acceptFiles ? (
          <input
            type="file"
            name="file"
            onChange={this.inputFilesSelected}
            accept={'video' === type ? 'video/*' : 'image/*,.ai,.eps,.pdf,.psd'}
            multiple={multiple}
            className={style.input}
            tabIndex="-1"
            ref={this.inputRef}
          />
        ) : null}
        {content ? content({ dragOver, progress, uploading: !acceptFiles }) : null}
      </label>
    )
  }
}
